import { CommonModule } from '@angular/common'
import { Component, ElementRef, Inject, ViewChild } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog'
import { UploadModalConfig, UploadType } from '@app/features/company-management/models/upload-modal-config'
import { CompanyManagementService } from '@app/features/company-management/service/company-management.service'
import { LibraryCascadeChangesModalComponent } from '@app/features/library-management/components/modals/library-cascade-changes-modal/library-cascade-changes-modal.component'
import { LibraryEntriesDuplicateMapRatecardsModalComponent } from '@app/features/library-management/components/modals/library-entries-duplicate-map-ratecards-modal/library-entries-duplicate-map-ratecards-modal.component'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { PipesModule } from '@app/shared/pipe/index.module'
import { SharedModule } from '@app/shared/shared.module'
import { User } from '@core/model/user.model'
import { AuthService } from '@core/service/auth.service'
import { LanguageService } from '@core/service/language.service'
import { ScopeUiAutocompleteComponent } from '@shared/components/ui-components/scope-ui-autocomplete/scope-ui-autocomplete.component'
import _ from 'lodash'
import { EMPTY, filter, forkJoin, map, switchMap, tap, throwError } from 'rxjs'
import * as XLSX from 'xlsx'

@Component({
  selector: 'upload-modal',
  standalone: true,
  imports: [CommonModule, PipesModule, MatDialogModule, SharedModule, ScopeUiAutocompleteComponent],
  templateUrl: './upload-modal.component.html',
  styleUrls: ['./upload-modal.component.scss'],
})
export class UploadModalComponent {
  fileName: string
  file: File
  errors: any[] = []
  verifyingOrImportingLoader = false
  errorText: any
  verifiedFile = false
  showErrorList = false
  fileUrl: string
  showGenerateTemplate = false
  selectedRateCard: any
  loggedInUser: User

  @ViewChild('uploader') uploader: ElementRef

  get showGenericTemplateButton() {
    return this.data.type !== UploadType.COMPONENT && this.data.type !== UploadType.FIXED_FEE_COMPONENT
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UploadModalConfig,
    public dialog: MatDialog,
    private lang: LanguageService,
    private manageService: CompanyManagementService,
    private authService: AuthService
  ) {
    this.loggedInUser = this.authService.loggedInUser
    this.errorText = {
      FILE_NOT_CORRECT: `File is not valid. Please use ${this.data.type === UploadType.WORD_OUTPUT ? '.docx' : '.xlsx'}`,
      IMPORT_FAILED: 'Error while uploading document, please try again',
      SECOND_PARTY_NOT_MATCH: 'Second Party name missing',
      SEE_ERRORS: 'Error while uploading document, please see errors',
      DEFAULT_ERROR: 'Value is not correct',
    }
  }

  uploadFile($event: any) {
    this.file = $event.target.files[0]
    this.fileName = $event.target.files[0].name
    this.verifiedFile = false
    this.errors = []
    this.verifiedFile = null
  }

  submit() {
    if (this.file && !this.verifyingOrImportingLoader) {
      const fileExt = this.fileName.split('.').pop().toLowerCase()
      if (
        (this.data.type !== UploadType.WORD_OUTPUT && fileExt.toLowerCase() === 'xlsx') ||
        (this.data.type === UploadType.WORD_OUTPUT && fileExt.toLowerCase() === 'docx')
      ) {
        this.verifyingOrImportingLoader = true
        this.data.submitFn(this.file).subscribe({
          next: (result) => {
            if (this.data.type === UploadType.RATECARD) {
              this.showCascadeChangesModal(result)
            } else {
              this.data.successFn(result)
            }
          },
          error: (error) => {
            if (error.status == 409) {
              this.showErrorList = true
              this.errors.push({ errorType: 'SEE_ERRORS' })
              const blob = new Blob([error.error], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              })
              const originalFile = this.file.name
              let verifiedFileName = 'Verified_'
              const dateFormat = new Date()
                .toISOString()
                .slice(0, 16)
                .replace('T', '_')
                .replace(':', '')
                .replaceAll('-', '')
              verifiedFileName = verifiedFileName.concat(dateFormat, '-', originalFile)
              this.fileName = verifiedFileName
              this.fileUrl = window.URL.createObjectURL(blob)
            }
            this.errors.push({ errorType: 'IMPORT_FAILED' })
            this.verifyingOrImportingLoader = false
            return throwError(error)
          },
        })
      } else {
        this.errors.push({ errorType: 'FILE_NOT_CORRECT' })
      }
    }
  }

  buildMapping(
    sourceRatecard: any,
    targetRatecard: any
  ): { mapping: Record<string, string | null>; isNeededToMap: boolean; mappingRoles: any[]; targetRoles: any[] } {
    const sourceDepartments = sourceRatecard?.departments
    const targetDepartments = targetRatecard?.departments
    if (!sourceDepartments || !targetDepartments) {
      return { mapping: {}, isNeededToMap: false, mappingRoles: [], targetRoles: [] }
    }

    const targetDepartmentsRolesByName: Record<string, any> = {}
    const targetRoles: any[] = []
    targetDepartments.forEach((td) =>
      td.roles.forEach((tr) => {
        targetDepartmentsRolesByName[tr.name.toLowerCase()] = tr
        targetRoles.push(tr)
      })
    )

    const distinctRolesMap: Record<string, any> = {}
    let isNeededToMap = false

    sourceDepartments.forEach((sd) =>
      sd.roles.forEach((role) => {
        const key = role.name.toLowerCase()
        if (!distinctRolesMap[key]) {
          const targetRole = targetDepartmentsRolesByName[key] ?? null
          if (!targetRole) {
            isNeededToMap = true
          }
          distinctRolesMap[key] = { id: role.id, name: role.name, targetMappingRole: targetRole, rate: role.rate }
        }
      })
    )

    const mappingRoles = Object.values(distinctRolesMap)
    const mapping = mappingRoles.reduce(
      (acc, mappingRole) => {
        acc[mappingRole.id] = mappingRole.targetMappingRole?.id ?? null
        return acc
      },
      {} as Record<string, string | null>
    )

    return { mapping, isNeededToMap, mappingRoles, targetRoles }
  }

  findFirstDiffByNameAndVersion(a: RatecardVersion[], b: RatecardVersion[]) {
    const oldRt = a.find((obj) => {
      const match = b.find((newRt) => newRt.name === obj.name)
      return match && match.version !== obj.version
    })
    if (!oldRt) return null
    const match = b.find((newRt) => newRt.name === oldRt.name)!
    return {
      name: oldRt.name,
      oldVersion: oldRt.version,
      newVersion: match.version,
      oldRatecard: oldRt,
      newRatecard: match,
    }
  }

  private showCascadeChangesModal(result: any) {
    const initialRatecards = this.data.ratecards
    this.manageService
      .getAllRateCards()
      .pipe(
        map((updatedRatecards) => this.findFirstDiffByNameAndVersion(initialRatecards, updatedRatecards)),
        tap((diff) => (!!diff && diff.newVersion !== diff.oldVersion ? {} : this.data.successFn(result))),
        filter((diff) => !!diff && diff.newVersion !== diff.oldVersion)
      )
      .subscribe((diff) => {
        if (diff) {
          this.dialog.open(LibraryCascadeChangesModalComponent, {
            data: {
              itemType: 'ratecard',
              description: 'The items will be updated if ratecard version is increased. Mapping may be required.',
              onSubmit: (result: boolean) => {
                if (result) {
                  forkJoin({
                    source: this.manageService.getRateCard(diff.oldRatecard.rateCardIdentity.id, diff.oldVersion),
                    target: this.manageService.getRateCard(diff.newRatecard.rateCardIdentity.id, diff.newVersion),
                  })
                    .pipe(
                      switchMap(({ source, target }) => {
                        const mappingResult = this.buildMapping(source, target)
                        if (!mappingResult.isNeededToMap) {
                          this.data.successFn(result)
                          return this.manageService.cascadeRatecardChangesToLibraryItemsAndTasks(
                            source,
                            target,
                            mappingResult.mapping
                          )
                        } else {
                          this.dialog.open(LibraryEntriesDuplicateMapRatecardsModalComponent, {
                            data: {
                              sourceRateCard: source,
                              targetRateCard: target,
                              mappingRoles: mappingResult.mappingRoles,
                              targetRoles: mappingResult.targetRoles,
                              onSubmit: (finalMapping: Record<string, string | null>) => {
                                this.data.successFn(result)
                                this.manageService
                                  .cascadeRatecardChangesToLibraryItemsAndTasks(source, target, finalMapping)
                                  .subscribe()
                              },
                            },
                          })
                          return EMPTY
                        }
                      })
                    )
                    .subscribe()
                } else {
                  this.data.successFn(result)
                }
              },
            },
          })
        }
      })
  }

  getInstructionUrl() {
    const staticUrl = 'https://scopebetter.zendesk.com/hc/en-gb/articles/'
    switch (this.data.type) {
      case UploadType.CLIENTS:
        return staticUrl + '360007333137-How-do-I-upload-clients'
      case UploadType.LOCATION:
        return staticUrl + '360007393938-How-do-I-upload-locations'
      case UploadType.CONTACT:
        return staticUrl + '360007394338-How-do-I-upload-contacts'
      case UploadType.BRAND:
        return staticUrl + '360007394858-How-do-I-upload-brands'
      case UploadType.RATECARD:
        return staticUrl + '360007390718-How-do-I-upload-a-Ratecard'
      case UploadType.DELIVERABLE:
        return staticUrl + '360007964497-How-do-I-upload-deliverables'
      case UploadType.FIXED_FEE_DELIVERABLE:
        return staticUrl + '28869840126749-How-do-I-upload-Fixed-Fee-Deliverables'
      case UploadType.TASK:
        return staticUrl + '23026786302109-How-do-I-upload-a-Task'
      case UploadType.COMPONENT:
        return staticUrl + '360007099138-How-do-I-upload-components'
      case UploadType.FIXED_FEE_COMPONENT:
        return staticUrl + '28889681134749-How-do-I-upload-Fixed-Fee-Components'
      case UploadType.DELIVERABLE_WITH_COMPONENTS:
        return staticUrl + '31049557974813-Copying-Deliverables-to-New-Disciplines'
      default:
        return ''
    }
  }

  onGenerateTemplate() {
    ;(this.data.type === UploadType.COMPONENT ||
      (this.data.type === UploadType.FIXED_FEE_COMPONENT && this.data.includeRoleAndHour) ||
      this.data.type === UploadType.TASK) &&
    this.loggedInUser.hasPrivilege('RATECARD__VIEW')
      ? (this.showGenerateTemplate = true)
      : this.getTemplateExampleUrl()
  }

  getTemplateExampleUrl() {
    let headerRow
    let rateCardRow
    switch (this.data.type) {
      case UploadType.CLIENTS:
        headerRow = [
          `${this.lang.get('second_party')} name`,
          'Rate card',
          'Relationship Since',
          'Output Template',
          'FTE',
        ]
        this.generateTemplate([headerRow], 'upload-clients.xlsx')
        break
      case UploadType.CONTACT:
        headerRow = ['Contact Name', `${this.lang.get('second_party')} Name`, 'Job Title', 'Phone', 'Location', 'Email']
        this.generateTemplate([headerRow], 'upload-contacts.xlsx')
        break
      case UploadType.LOCATION:
        headerRow = [
          'Address Name',
          'Address 1',
          'Address 2',
          'City',
          'Post Code',
          'Country',
          'Phone',
          `${this.lang.get('second_party')} Name`,
        ]
        this.generateTemplate([headerRow], 'upload-locations.xlsx')
        break
      case UploadType.BRAND:
        headerRow = [
          'Brand Name',
          `${this.lang.get('second_party')} Name`,
          'Rate card',
          'Location',
          'Key Contact',
          'Output Template',
          'Relationship Since',
        ]
        this.generateTemplate([headerRow], 'upload-brands.xlsx')
        break
      case UploadType.RATECARD:
        headerRow = ['Department Name', `${this.lang.get('agency')} Role`, 'Location', 'Rate', 'Cost']
        rateCardRow = ['Rate-card Name', '', 'Currency', '', 'Scope Mark Card', 'N']
        this.manageService.getRateCardMetaStructure().subscribe({
          next: (result: any) => {
            if (result && result.ratecardMetaStructureDefinition) {
              result.ratecardMetaStructureDefinition.roles.additionalFields.forEach((role) => {
                headerRow.push(role.name)
              })
            }
            this.generateTemplate([rateCardRow, headerRow], 'upload-ratecard.xlsx')
          },
          error: () => {
            console.error('Failed to load the company rate card meta structure.')
          },
        })
        break
      case UploadType.DELIVERABLE:
        headerRow = [
          `${this.lang.get('deliverable')} Name`,
          'Discipline',
          `${this.lang.get('deliverable')} description`,
          `${this.lang.get('deliverable')} internal note`,
          'Rate-card Name',
        ]
        this.generateTemplate([headerRow], 'upload-deliverables-template.xlsx')
        break
      case UploadType.FIXED_FEE_DELIVERABLE:
        headerRow = [
          `${this.lang.get('deliverable')} Name`,
          'Discipline',
          `${this.lang.get('deliverable')} description`,
          `${this.lang.get('deliverable')} internal note`,
          'Rate-card Name',
          'Fixed Fee Price',
          'Fixed Fee Price Editable (Y/N)',
        ]
        this.generateTemplate([headerRow], 'upload-fixed-fee-deliverables-template.xlsx')
        break
      case UploadType.COMPONENT:
        rateCardRow = ['Rate-card Name']
        headerRow = ['Name', 'Discipline', 'Description', 'Internal note', 'Complexity']
        this.generateTemplate([rateCardRow, headerRow], 'upload-components-template.xlsx')
        break
      case UploadType.FIXED_FEE_COMPONENT:
        rateCardRow = ['Rate-card Name']
        headerRow = [
          'Fixed Fee Component Name',
          'Discipline',
          'Description',
          'Internal note',
          'Complexity (S-XL)',
          this.data.includeRoleAndHour ? '' : 'Fixed Fee Cost',
          'Fixed Fee Price',
          'Fixed Fee Price Editable (Y/N)',
        ].filter((r) => r.length)
        this.generateTemplate(
          [rateCardRow, headerRow],
          `upload-fixed-fee-${this.data.includeRoleAndHour ? '' : 'no-roles-and-hours-'}components-template.xlsx`
        )
        break
      case UploadType.TASK:
        rateCardRow = ['Rate-card Name', 'Rate Card 1']
        headerRow = ['Task Name', 'Task description', 'Department 1', '', '', '', 'Department 2', '', '', '']
        const rolesRow = ['', '', '', '', 'Role 1', 'Role 2', 'Role 3', '', 'Role 4', 'Role 5', 'Role 6']
        const valuesRow = ['Task 1', 'My description', '', '1', '2', '3', '', '4', '', '']
        this.generateTemplate(
          [rateCardRow, headerRow, rolesRow, valuesRow],
          'generic-upload-tasks-template.xlsx',
          'Rate Card 1'
        )
        break
      case UploadType.DELIVERABLE_WITH_COMPONENTS:
        headerRow = [
          'Deliverable Name',
          `Discipline`,
          'Deliverable description',
          'Deliverable extra description',
          'Deliverable internal note',
          'Rate-card Name',
          'Component Name',
          'Component Complexity',
          'Component Quantity',
        ]
        this.generateTemplate([headerRow], 'duplicate-deliverables.xlsx')
        break
    }
  }

  generateTemplate(rows: string[][], name: string, sheetName?: string) {
    const ws = XLSX.utils.aoa_to_sheet(rows)

    ;/!* add to workbook *!/
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, sheetName)
    ;/!* write workbook and force a download *!/
    XLSX.writeFile(wb, name)
  }

  validateExcelFile() {
    if (this.file) {
      const fileName = this.file.name
      const fileExt = fileName.split('.').pop().toLowerCase()
      this.errors = []

      if (fileExt.toLowerCase() === 'xlsx') {
        if (!this.verifyingOrImportingLoader) {
          this.verifyingOrImportingLoader = true
          this.verifiedFile = false

          this.data.verifyService.verify(this.file, this.data.type, this.data.includeRoleAndHour).subscribe({
            next: (context: any) => {
              if (context?.byteLength > 0) {
                const blob = new Blob([context], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                })
                const url = window.URL.createObjectURL(blob)
                window.open(url, '_self', '')
                this.showErrorList = true
                this.verifyingOrImportingLoader = false
                this.uploader.nativeElement.value(null)
              } else {
                this.verifiedFile = true
                this.verifyingOrImportingLoader = false
              }
            },
            error: (error) => {
              if (error.status == 409) {
                this.showErrorList = true
                this.errors.push({ errorType: 'SEE_ERRORS' })
                const blob = new Blob([error.error], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                })
                const originalFile = this.file.name
                let verifiedFileName = 'Verified_'
                const dateFormat = new Date()
                  .toISOString()
                  .slice(0, 16)
                  .replace('T', '_')
                  .replace(':', '')
                  .replaceAll('-', '')
                verifiedFileName = verifiedFileName.concat(dateFormat, '-', originalFile)
                this.fileName = verifiedFileName
                this.fileUrl = window.URL.createObjectURL(blob)
              } else {
                this.errors.push({ errorType: 'FILE_NOT_CORRECT' })
              }

              this.verifiedFile = true
              this.verifyingOrImportingLoader = false
            },
          })
        }
      } else {
        this.errors = []
        this.errors.push({ errorType: 'FILE_NOT_CORRECT' })
      }
    }
  }

  downloadConflictedFile() {
    const a = document.createElement('a')
    document.body.appendChild(a)
    a.href = this.fileUrl
    a.download = this.fileName
    a.click()
  }

  generateRateCardTemplate() {
    if (!this.selectedRateCard) return
    this.manageService.getRateCard(this.selectedRateCard.rateCardIdentity.id).subscribe({
      next: (rc: any) => {
        let sheetName = rc.name
        if (rc.name.length > 31) {
          sheetName = rc.name.substring(0, 28) + '...'
        }
        const rateCardRow = ['Rate-card Name', rc.name]
        let headerRow
        let rolesRow
        let fileName

        switch (this.data.type) {
          case UploadType.COMPONENT:
            headerRow = ['Name', 'Discipline', 'Description', 'Internal note', 'Complexity']
            rolesRow = ['', '', '', '', '']
            fileName = `${rc.name}-upload-components-template.xlsx`
            break
          case UploadType.FIXED_FEE_COMPONENT:
            headerRow = [
              'Fixed Fee Component Name',
              'Discipline',
              'Description',
              'Internal note',
              'Complexity (S-XL)',
              this.data.includeRoleAndHour ? '' : 'Fixed Fee Cost',
              'Fixed Fee Price',
              'Fixed Fee Price Editable (Y/N)',
            ].filter((r) => r.length)
            fileName = `${rc.name}-upload-fixed-fee-${this.data.includeRoleAndHour ? '' : 'no-roles-and-hours-'}component-template.xlsx`
            if (this.data.includeRoleAndHour) rolesRow = ['', '', '', '', '', '', '']
            break
          case UploadType.TASK:
            headerRow = ['Task Name', 'Task description']
            rolesRow = ['', '']
            fileName = `${rc.name}-upload-tasks-template.xlsx`
            break
        }

        if (rolesRow) {
          _.sortBy(rc.departments, 'name').forEach((dep) => {
            rolesRow.push(``)
            headerRow.push(`${dep.name}`)
            _.sortBy(dep.roles, 'order').forEach((role) => {
              headerRow.push(``)
              rolesRow.push(`${role.name}`)
            })
          })
        }

        this.generateTemplate([rateCardRow, headerRow, rolesRow], fileName, sheetName)
      },
    })
  }

  protected readonly UploadType = UploadType
}
